import React, { useState, useCallback, useEffect } from 'react';
import { 
  Clipboard, 
  Upload, 
  Wand2, 
  CheckCircle, 
  AlertCircle,
  Copy,
  RefreshCw,
  ExternalLink,
  Code,
  Zap,
  TestTube,
  Database,
  Key,
  Globe,
  Cpu,
  AlertTriangle,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { EnhancedConfigParser } from '@/services/EnhancedConfigParser';
import { ParsedConfig, ProviderType } from '@/types';
import { useEnhancedAppStore } from '@/stores/EnhancedAppStore';
import { ApiService } from '@/services/ApiService';
import clsx from 'clsx';

// 示例配置文本
const EXAMPLE_CONFIGS = [
  {
    name: 'OpenAI 兼容 API',
    content: `OPENAI_BASE_URL=https://hellofriend.eu.cc/v1
OPENAI_API_KEY=sk-OQh6HMgDGrshEJ4edukDGmjMKD0ZbjLPuhgKvYD89mxJnmls
OPENAI_MODEL=glm5`,
    description: '自定义 OpenAI 兼容 API',
  },
  {
    name: '标准 OpenAI',
    content: `export OPENAI_API_KEY=sk-abc123def456ghi789jkl012mno345pqr678stu901
export OPENAI_BASE_URL=https://api.openai.com
export OPENAI_MODEL=gpt-4-turbo-preview`,
    description: '标准 OpenAI API 配置',
  },
  {
    name: '环境变量格式',
    content: `API_KEY=sk-1234567890abcdef
BASE_URL=https://api.anthropic.com
MODEL=claude-3-opus`,
    description: '通用环境变量格式',
  },
  {
    name: 'JSON 配置',
    content: `{
  "api": {
    "base_url": "https://generativelanguage.googleapis.com/v1",
    "api_key": "AIzaSyB...",
    "model": "gemini-pro"
  },
  "settings": {
    "temperature": 0.7,
    "max_tokens": 1000
  }
}`,
    description: 'JSON 格式配置',
  },
  {
    name: '代码片段',
    content: `const config = {
  baseUrl: 'https://api.openai.com/v1',
  apiKey: process.env.OPENAI_API_KEY,
  model: 'gpt-4',
  temperature: 0.8,
  maxTokens: 2000
};`,
    description: 'JavaScript 代码片段',
  },
  {
    name: '简单文本',
    content: `我的API密钥是 sk-test1234567890abcdef1234567890
API地址是 https://myapi.example.com/v1
使用 gpt-3.5-turbo 模型`,
    description: '自由文本格式',
  },
];

const PROVIDER_TYPE_LABELS: Record<ProviderType, { label: string; color: string; icon: React.ReactNode }> = {
  [ProviderType.OPENAI]: {
    label: 'OpenAI',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: <Zap className="w-4 h-4" />,
  },
  [ProviderType.ANTHROPIC]: {
    label: 'Anthropic',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: <Cpu className="w-4 h-4" />,
  },
  [ProviderType.GOOGLE_AI]: {
    label: 'Google AI',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: <Database className="w-4 h-4" />,
  },
  [ProviderType.AZURE_OPENAI]: {
    label: 'Azure OpenAI',
    color: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    icon: <Cloud className="w-4 h-4" />,
  },
  [ProviderType.CUSTOM]: {
    label: '自定义 API',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: <Globe className="w-4 h-4" />,
  },
};

const Cloud = (props: React.SVGProps<SVGSVGElement>) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
  </svg>
);

export const EnhancedConfigParserPage: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [parsedConfig, setParsedConfig] = useState<ParsedConfig | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [selectedExample, setSelectedExample] = useState<number | null>(null);
  const [validationResult, setValidationResult] = useState<{ valid: boolean; errors: string[]; warnings: string[] } | null>(null);
  
  const { parseConfig, validateAndCompleteConfig, addProviderWithTest } = useEnhancedAppStore();

  // 处理粘贴
  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      setInputText(text);
      // 自动解析
      setTimeout(() => handleParse(), 100);
    } catch (error) {
      // 降级方案
      const textArea = document.createElement('textarea');
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      textArea.value = 'Paste here...';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('paste');
      const pastedText = textArea.value;
      document.body.removeChild(textArea);
      setInputText(pastedText);
      setTimeout(() => handleParse(), 100);
    }
  }, []);

  // 解析配置
  const handleParse = useCallback(() => {
    if (!inputText.trim()) return;

    setIsParsing(true);
    setTimeout(() => {
      try {
        const result = parseConfig(inputText);
        setParsedConfig(result);
        
        // 验证配置
        const validation = EnhancedConfigParser.validateConfig(result);
        setValidationResult(validation);
        
        // 重置测试结果
        setTestResult(null);
      } catch (error) {
        console.error('解析失败:', error);
        setParsedConfig(null);
        setValidationResult({
          valid: false,
          errors: ['解析失败: ' + (error instanceof Error ? error.message : '未知错误')],
          warnings: [],
        });
      } finally {
        setIsParsing(false);
      }
    }, 300);
  }, [inputText, parseConfig]);

  // 测试连接
  const handleTestConnection = useCallback(async () => {
    if (!parsedConfig?.baseUrl || !parsedConfig?.apiKey) return;

    setIsTesting(true);
    try {
      const result = await ApiService.testConnection({
        baseUrl: parsedConfig.baseUrl,
        apiKey: parsedConfig.apiKey,
        providerType: parsedConfig.providerType || ProviderType.CUSTOM,
      });
      
      setTestResult(result);
      
      // 如果测试成功，更新配置中的模型列表
      if (result.success && result.models && result.models.length > 0) {
        setParsedConfig(prev => prev ? {
          ...prev,
          additionalConfig: {
            ...prev.additionalConfig,
            availableModels: result.models,
          },
        } : null);
      }
    } catch (error) {
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : '测试失败',
      });
    } finally {
      setIsTesting(false);
    }
  }, [parsedConfig]);

  // 添加到供应商列表
  const handleAddToProviders = useCallback(async () => {
    if (!parsedConfig?.baseUrl || !parsedConfig?.apiKey) return;

    const providerName = parsedConfig.additionalConfig?.name || 
                        `供应商_${new Date().toLocaleTimeString()}`;
    
    const providerData = {
      name: providerName,
      baseUrl: parsedConfig.baseUrl,
      apiKey: parsedConfig.apiKey,
      providerType: parsedConfig.providerType || ProviderType.CUSTOM,
      models: testResult?.models || [],
      defaultModel: parsedConfig.model,
      config: {
        temperature: 0.7,
        maxTokens: 1000,
        ...parsedConfig.additionalConfig,
      },
    };

    try {
      const result = await addProviderWithTest(providerData, true);
      
      if (result.testResult?.success) {
        alert(`✅ 供应商添加成功！\n模型数量: ${result.testResult.models?.length || 0}`);
      } else {
        alert('⚠️ 供应商已添加，但连接测试失败');
      }
    } catch (error) {
      alert('❌ 添加供应商失败: ' + (error instanceof Error ? error.message : '未知错误'));
    }
  }, [parsedConfig, testResult, addProviderWithTest]);

  // 加载示例
  const loadExample = useCallback((index: number) => {
    setSelectedExample(index);
    setInputText(EXAMPLE_CONFIGS[index].content);
    setTimeout(() => handleParse(), 100);
  }, [handleParse]);

  // 清空所有
  const handleClear = useCallback(() => {
    setInputText('');
    setParsedConfig(null);
    setTestResult(null);
    setValidationResult(null);
    setSelectedExample(null);
  }, []);

  // 自动解析
  useEffect(() => {
    if (inputText.trim().length > 50) {
      const timeoutId = setTimeout(() => {
        if (!isParsing) {
          handleParse();
        }
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [inputText, isParsing, handleParse]);

  return (
    <div className="space-y-6">
      {/* 标题和说明 */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-primary-600" />
              智能配置识别
            </h1>
            <p className="text-gray-600 mt-2">
              粘贴任何格式的 API 配置，系统将自动识别 URL、API 密钥和供应商类型。
              支持环境变量、JSON、代码片段和自由文本。
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClear}
              className="btn-secondary"
            >
              清空
            </button>
            <button
              onClick={handlePaste}
              className="btn-primary"
            >
              <Clipboard className="w-4 h-4 mr-2" />
              粘贴并解析
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 左侧：输入区域 */}
        <div className="space-y-4">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Code className="w-5 h-5" />
              输入配置文本
            </h3>
            
            <div className="mb-4">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={`粘贴您的 API 配置文本...
例如：
OPENAI_BASE_URL=https://api.openai.com/v1
OPENAI_API_KEY=sk-abc123...
MODEL=gpt-4`}
                className="input min-h-[200px] font-mono text-sm"
                spellCheck={false}
              />
            </div>

            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500">
                {inputText.length} 字符
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleParse}
                  disabled={!inputText.trim() || isParsing}
                  className={clsx(
                    'btn-primary',
                    isParsing && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {isParsing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      解析中...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      智能解析
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* 示例配置 */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Copy className="w-5 h-5" />
              示例配置
            </h3>
            <div className="grid grid-cols-1 gap-2">
              {EXAMPLE_CONFIGS.map((example, index) => (
                <button
                  key={index}
                  onClick={() => loadExample(index)}
                  className={clsx(
                    'p-3 text-left rounded-lg border transition-all',
                    selectedExample === index
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                  )}
                >
                  <div className="font-medium text-gray-900">{example.name}</div>
                  <div className="text-sm text-gray-500 mt-1">{example.description}</div>
                  <div className="text-xs text-gray-400 mt-2 truncate">
                    {example.content.split('\n')[0]}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 右侧：解析结果 */}
        <div className="space-y-4">
          {/* 解析结果概览 */}
          {parsedConfig && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                解析结果
                <div className="ml-auto">
                  <span className={clsx(
                    'px-2 py-1 rounded-full text-xs font-medium',
                    parsedConfig.confidence > 0.8
                      ? 'bg-green-100 text-green-800'
                      : parsedConfig.confidence > 0.6
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  )}>
                    置信度: {(parsedConfig.confidence * 100).toFixed(0)}%
                  </span>
                </div>
              </h3>

              {/* 供应商类型 */}
              {parsedConfig.providerType && (
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-2">供应商类型</div>
                  <div className={clsx(
                    'inline-flex items-center gap-2 px-3 py-2 rounded-lg border',
                    PROVIDER_TYPE_LABELS[parsedConfig.providerType].color
                  )}>
                    {PROVIDER_TYPE_LABELS[parsedConfig.providerType].icon}
                    {PROVIDER_TYPE_LABELS[parsedConfig.providerType].label}
                  </div>
                </div>
              )}

              {/* URL */}
              {parsedConfig.baseUrl && (
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    API 端点
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 font-mono text-sm break-all">
                    {parsedConfig.baseUrl}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {EnhancedConfigParser.cleanAndCompleteUrl(parsedConfig.baseUrl, parsedConfig.providerType) !== parsedConfig.baseUrl && (
                      <>自动补全为: {EnhancedConfigParser.cleanAndCompleteUrl(parsedConfig.baseUrl, parsedConfig.providerType)}</>
                    )}
                  </div>
                </div>
              )}

              {/* API 密钥 */}
              {parsedConfig.apiKey && (
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    API 密钥
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 font-mono text-sm">
                    {parsedConfig.apiKey.slice(0, 8)}...{parsedConfig.apiKey.slice(-4)}
                    <button
                      onClick={() => navigator.clipboard.writeText(parsedConfig.apiKey!)}
                      className="ml-2 text-primary-600 hover:text-primary-800"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    格式: {ApiService.validateApiKeyFormat(parsedConfig.apiKey, parsedConfig.providerType) ? '✅ 有效' : '⚠️ 可能无效'}
                  </div>
                </div>
              )}

              {/* 模型 */}
              {parsedConfig.model && (
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-2">检测到的模型</div>
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    {parsedConfig.model}
                  </div>
                </div>
              )}

              {/* 验证结果 */}
              {validationResult && (
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-2">验证结果</div>
                  {validationResult.errors.length > 0 && (
                    <div className="mb-2">
                      {validationResult.errors.map((error, index) => (
                        <div key={index} className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg mb-1">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">{error}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {validationResult.warnings.length > 0 && (
                    <div className="mb-2">
                      {validationResult.warnings.map((warning, index) => (
                        <div key={index} className="flex items-start gap-2 p-3 bg-yellow-50 text-yellow-700 rounded-lg mb-1">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">{warning}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {validationResult.errors.length === 0 && validationResult.warnings.length === 0 && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg">
                      <CheckCircle className="w-4 h-4" />
                      <div className="text-sm">配置验证通过</div>
                    </div>
                  )}
                </div>
              )}

              {/* 操作按钮 */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleTestConnection}
                  disabled={!parsedConfig?.baseUrl || !parsedConfig?.apiKey || isTesting}
                  className={clsx(
                    'btn-primary flex-1',
                    (!parsedConfig?.baseUrl || !parsedConfig?.apiKey || isTesting) && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {isTesting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      测试中...
                    </>
                  ) : (
                    <>
                      <TestTube className="w-4 h-4 mr-2" />
                      测试连接
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleAddToProviders}
                  disabled={!parsedConfig?.baseUrl || !parsedConfig?.apiKey}
                  className={clsx(
                    'btn-accent flex-1',
                    (!parsedConfig?.baseUrl || !parsedConfig?.apiKey) && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <Database className="w-4 h-4 mr-2" />
                  添加到供应商
                </button>
              </div>
            </div>
          )}

          {/* 测试结果 */}
          {testResult && (
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5" />
                连接测试结果
              </h3>
              
              <div className={clsx(
                'p-4 rounded-lg mb-4',
                testResult.success
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
              )}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                  <div className="font-medium">
                    {testResult.success ? '连接成功' : '连接失败'}
                  </div>
                </div>
                
                {testResult.latency && (
                  <div className="text-sm mb-2">
                    延迟: <span className="font-mono">{testResult.latency}ms</span>
                  </div>
                )}
                
                {testResult.error && (
                  <div className="text-sm">{testResult.error}</div>
                )}
              </div>

              {/* 模型列表 */}
              {testResult.models && testResult.models.length > 0 && (
                <div className="mb-4">
                  <div className="text-sm font-medium text-gray-700 mb-2">可用模型</div>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {testResult.models.map((model: string, index: number) => (
                      <div
                        key={index}
                        className="p-2 bg-gray-50 rounded border border-gray-200 text-sm font-mono truncate"
                        title={model}
                      >
                        {model}
                      </div>
                    ))}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    共 {testResult.models.length} 个模型
                  </div>
                </div>
              )}

              {/* 供应商信息 */}
              {testResult.providerInfo && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">供应商信息</div>
                  <div className="space-y-2">
                    {testResult.providerInfo.version && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">版本:</span>
                        <span className="font-mono">{testResult.providerInfo.version}</span>
                      </div>
                    )}
                    {testResult.providerInfo.capabilities && testResult.providerInfo.capabilities.length > 0 && (
                      <div>
                        <div className="text-gray-600 mb-1">支持功能:</div>
                        <div className="flex flex-wrap gap-1">
                          {testResult.providerInfo.capabilities.map((cap: string, index: number) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded"
                            >
                              {cap}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 空状态 */}
          {!parsedConfig && (
            <div className="card">
              <div className="text-center py-12">
                <Wand2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">等待输入</h3>
                <p className="text-gray-500 mb-6">
                  粘贴您的 API 配置文本，系统将自动识别 URL、API 密钥和供应商类型。
                </p>
                <div className="space-y-3 max-w-md mx-auto">
                  <button
                    onClick={handlePaste}
                    className="btn-primary w-full"
                  >
                    <Clipboard className="w-4 h-4 mr-2" />
                    从剪贴板粘贴
                  </button>
                  <div className="text-sm text-gray-500">
                    或从左侧选择示例配置
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* API 端点信息 */}
      {parsedConfig?.baseUrl && parsedConfig.providerType && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ExternalLink className="w-5 h-5" />
            API 端点信息
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(
              EnhancedConfigParser.getApiEndpointInfo(parsedConfig.baseUrl, parsedConfig.providerType)
            ).map(([key, value]) => (
              <div key={key} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-sm font-medium text-gray-700 mb-1">
                  {key === 'chatEndpoint' && '聊天端点'}
                  {key === 'modelsEndpoint' && '模型端点'}
                  {key === 'healthEndpoint' && '健康检查'}
                </div>
                <div className="font-mono text-sm text-gray-900 break-all">
                  {value}
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(value)}
                  className="mt-2 text-xs text-primary-600 hover:text-primary-800 flex items-center gap-1"
                >
                  <Copy className="w-3 h-3" />
                  复制
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 使用说明 */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">使用说明</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-bold">1</span>
              </div>
              <div className="font-medium">粘贴配置</div>
            </div>
            <p className="text-sm text-gray-600">
              支持任何格式：环境变量、JSON、代码片段、自由文本。系统会自动识别关键信息。
            </p>
          </div>
          
          <div className="p-4 bg-green-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                <span className="text-green-600 font-bold">2</span>
              </div>
              <div className="font-medium">测试连接</div>
            </div>
            <p className="text-sm text-gray-600">
              一键测试 API 连通性，获取可用模型列表，验证配置是否正确。
            </p>
          </div>
          
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                <span className="text-purple-600 font-bold">3</span>
              </div>
              <div className="font-medium">添加到管理</div>
            </div>
            <p className="text-sm text-gray-600">
              将验证通过的配置添加到供应商列表，方便后续管理和使用。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};